import { useAppSelector, useAppDispatch } from "@/services/hooks";
import React, { useEffect, useMemo, useState, Fragment } from "react";
import { Button, Label, Media } from "reactstrap";
import { Link } from "react-router";

import { changeStatus } from "@/services/userServices/authActions";
import { deleteUser } from "@/services/userServices/userActions";
import UsersListView from "./UsersListView";
import ReactBSAlert from "react-bootstrap-sweetalert";
import NotificationAlertOptions from "@/layouts/Alerts";
import type { SitePermission } from "@/services/userServices/useCurrentPermission";

/** A user of the list (GET /api/users/), as the columns, the permission alert and the role modal read it. */
export type ListedUser = {
  _id: string;
  name: string;
  email?: string;
  role?: string;
  avatar?: string;
  enabled?: boolean;
  countrycode?: { label?: string }[];
  permission?: SitePermission[];
};

/** The user the role modal edits: `{}` until a row's "Edit role" is clicked. */
export type EditableUser = Partial<ListedUser>;

type UserCellProps = { row: { original: ListedUser } };

export type UsersListContainerProps = {
  users?: ListedUser[];
  permission?: Readonly<SitePermission> | null;
  // Method syntax on purpose (as ImageUploadContainer's ImageUpload): UsersContainer
  // passes a handler typed for its own narrower row, which a method parameter accepts.
  updateRoleHandler?(user: EditableUser): void | Promise<void>;
  cleaningDeletedUserState?(userId: string): void | Promise<void>;
};

const UsersListContainer = ({
  users: usersFromProps,
  permission,
  updateRoleHandler,
  cleaningDeletedUserState,
}: UsersListContainerProps) => {
  const dispatch = useAppDispatch();

  // Kept as state, not derived: onToggleEnabled updates it optimistically once
  // the status PUT succeeds, so it is more than a mirror of the prop.
  const [users, setUsers] = useState<ListedUser[]>([]);

  const [formModal, setFormModal] = useState(false);
  const [userUpdateRole, setUserUpdateRole] = useState<EditableUser>({});
  const [alert, setAlert] = useState<React.ReactNode>(null);
  
  useEffect(() => {
    if (usersFromProps && Array.isArray(usersFromProps)) {
      setUsers(usersFromProps);
    }
  }, [usersFromProps]);

  const notify = (type: string, msg: string) => NotificationAlertOptions(type, msg);

  const onToggleModal = (userToUpdate?: EditableUser) => {
    setUserUpdateRole(userToUpdate ?? {});
    setFormModal((v) => !v);
  };
  const onBasicAlert = (_e: React.MouseEvent, user: ListedUser) => {
    // Every row of GET /api/users/ carries `permission`; the JavaScript read it unguarded.
    const permissions = user.permission!;
    setAlert(
      // @ts-expect-error react-bootstrap-sweetalert prop-types (customIcon/btnSize)
      <ReactBSAlert
        style={{ display: "block", marginTop: "-100px" }}
        title={`${user.name} Permissions (${permissions.length})`}
        onConfirm={() => onHideAlert()}
        onCancel={() => onHideAlert()}
        btnSize=""
      >
        {permissions.map((perm) => (
          <Fragment key={`${perm.siteDomain}|${perm.CountryCode}`}>
            <blockquote className="blockquote text-center">
              <p className="mb-0">
                <strong className="text-primary">
                  {`${perm.siteDomain} (${perm.CountryCode})`}
                </strong>
              </p>
              <footer className="blockquote-footer">
                <div className="row">
                  <div className="col-sm-4">
                    <p className="mb-0 text-default">Read Only</p>
                    {(perm.read === true && perm.edit === false && perm.publish === false)
                      ? <i className="fas fa-check text-success fa-1x" />
                      : <i className="ni ni-fat-remove text-warning fa-1x" />}
                  </div>
                  <div className="col-sm-4">
                    <p className="mb-0 text-default">Read & Edit</p>
                    {(perm.edit === true && perm.publish === false)
                      ? <i className="fas fa-check text-success fa-1x" />
                      : <i className="ni ni-fat-remove text-warning fa-1x" />}
                  </div>
                  <div className="col-sm-4">
                    <p className="mb-0 text-default">Read & Edit & publish</p>
                    {perm.publish === true
                      ? <i className="fas fa-check text-success fa-1x" />
                      : <i className="ni ni-fat-remove text-warning fa-1x" />}
                  </div>
                </div>
              </footer>
            </blockquote>
          </Fragment>
        ))}
      </ReactBSAlert>
    );
  };
  const onHideAlert = () => setAlert(null);
  const onToggleEnabled = async (e: React.ChangeEvent<HTMLInputElement>, user: ListedUser) => {
    const updated = { ...user, enabled: e.target.checked };
    // Only apply the optimistic list update when the status PUT succeeds; changeStatus now
    // re-throws on failure (SCRUM-815), so the toggle no longer diverges from the server.
    try {
      await dispatch(changeStatus(updated));
      setUsers((prev) => prev.map((u) => (u._id === updated._id ? updated : u)));
    } catch (err) {
      // Errors are surfaced by the shell hub (GlobalNotifications) from state.errors;
      // changeStatus dispatches GET_ERRORS itself, so a local toast here rendered a
      // second, identical one on this view's default ToastContainer (SCRUM-1039).
    }
  };
  const onQuestionAlert = (e: React.MouseEvent, user: ListedUser) => {
    e.preventDefault();
    setAlert(
      // @ts-expect-error react-bootstrap-sweetalert prop-types (customIcon/btnSize)
      <ReactBSAlert
        custom
        style={{ display: "block", marginTop: "-100px" }}
        title="Delete User Operation"
        customIcon={
          <div className="swal2-icon swal2-question swal2-animate-question-icon text-warning" style={{ display: "flex" }}>
            <span className="swal2-icon-text">?</span>
          </div>
        }
        onConfirm={async () => {
          onHideAlert();
          // Only run cleanup + show success when the delete succeeds; deleteUser now
          // re-throws on failure (SCRUM-815).
          try {
            // SCRUM-1033: deleteUser is a createAppAsyncThunk now, so .unwrap() is
            // what turns a failed DELETE back into a rejection — without it `await
            // dispatch(...)` resolves even on failure and the SCRUM-815 false
            // "deleted successfully" toast returns.
            await dispatch(deleteUser(user._id)).unwrap();
            if (typeof cleaningDeletedUserState === "function") {
              await cleaningDeletedUserState(user._id);
            }
            notify("success", "User has been deleted Successfully!");
          } catch (err) {
            // Errors are surfaced by the shell hub (GlobalNotifications) from
            // state.errors; deleteUser is a createAppAsyncThunk, so appErrorMiddleware
            // already feeds its displayable payload there. A local toast here rendered
            // a second, identical one on this view's default ToastContainer
            // (SCRUM-1039). Success still toasts locally — only the error channel
            // moves to the hub.
          }
        }}
        onCancel={() => onHideAlert()}
        confirmBtnBsStyle="warning"
        confirmBtnText="Yes I'm sure"
        btnSize=""
      >
        Are you sure to delete <strong className="text-primary">{` ${user.name}`}</strong> account!
      </ReactBSAlert>
    );
  };

  /**
   * SCRUM-1141: search, sort and paging come from TableWrapper, so the container
   * only describes the columns. Every cell that renders markup rather than a
   * field carries an explicit `id`, and each cell component is named so
   * `react/display-name` (an eslint error here) stays satisfied.
   *
   * `permission` may be null, not just undefined — authReducer normalises a
   * no-match / not-yet-loaded site to null (SCRUM-856) and UsersContainer
   * forwards it raw — so the gate below keeps the optional chaining SCRUM-946
   * introduced.
   */
  const columns = useMemo(() => {
    const NameCell = ({ row }: UserCellProps) => {
      const u = row.original;
      return (
        <Media className="align-items-center">
          <a
            className="avatar rounded-circle mr-3"
            href="#picture"
            onClick={(e) => e.preventDefault()}
          >
            <img alt={`${u.name} picture`} src={u.avatar} style={{ width: 48, height: 48 }} />
          </a>
          <Media>
            <span className="mb-0 text-sm">{u.name}</span>
          </Media>
        </Media>
      );
    };
    NameCell.displayName = "UserNameCell";

    // Every row of GET /api/users/ carries `countrycode`; the JavaScript read it unguarded.
    const CountryCell = ({ row }: UserCellProps) => (
      <>
        {row.original.countrycode!.map((c) => (
          <React.Fragment key={c.label}>
            <Label>{c.label}</Label>
            <br />
          </React.Fragment>
        ))}
      </>
    );
    CountryCell.displayName = "UserCountryCell";

    const EnabledCell = ({ row }: UserCellProps) =>
      row.original.enabled ? (
        <i className="fas fa-check text-success fa-1x" />
      ) : (
        <i className="ni ni-fat-remove text-warning fa-1x" />
      );
    EnabledCell.displayName = "UserEnabledCell";

    const PermissionsCell = ({ row }: UserCellProps) => (
      <Button
        className="btn-icon-only rounded-circle"
        color="white"
        href="#users"
        onClick={(e) => onBasicAlert(e, row.original)}
      >
        <i className="ni ni-active-40 text-primary"></i>
      </Button>
    );
    PermissionsCell.displayName = "UserPermissionsCell";

    const base = [
      { accessorKey: "name", header: "Name", cell: NameCell },
      { accessorKey: "email", header: "Email" },
      { accessorKey: "role", header: "Role" },
      { id: "countrycode", header: "Country access", cell: CountryCell },
      { id: "enabled", header: "Enabled", cell: EnabledCell },
      { id: "permissions", header: "Permissions", cell: PermissionsCell },
    ];

    if (!(permission?.edit || permission?.publish)) return base;

    const ActionsCell = ({ row }: UserCellProps) => {
      const u = row.original;
      return (
        <div className="row" style={{ marginRight: 0 }}>
          <div className="col-auto">
            <Link
              to={`/admin/update-permission/${u._id}`}
              className="btn-sm btn-outline-primary mr-1"
            >
              Edit permission
            </Link>
          </div>
          <div className="col-auto">
            <Button
              className="btn-sm btn-outline-primary mr-1"
              onClick={() => onToggleModal(u)}
              style={{ background: "none!important", border: "none", padding: 0 }}
            >
              Edit role
            </Button>
          </div>
          <div className="col-auto">
            <label className="custom-toggle custom-toggle-primary mr-1">
              <input
                type="checkbox"
                checked={u.enabled}
                onChange={(e) => onToggleEnabled(e, u)}
              />
              <span
                className="custom-toggle-slider rounded-circle"
                data-label-off="enable"
                data-label-on="disable"
              />
            </label>
          </div>
          <div className="col-auto">
            <Button
              className="mr-4"
              color="danger"
              size="sm"
              onClick={(e) => onQuestionAlert(e, u)}
            >
              <i className="fas fa-trash" />
            </Button>
          </div>
        </div>
      );
    };
    ActionsCell.displayName = "UserActionsCell";

    return [...base, { id: "actions", header: "Actions", cell: ActionsCell }];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permission]);

  return (
    <UsersListView
      users={users}
      columns={columns}
      alert={alert}
      formModal={formModal}
      onToggleModal={onToggleModal}
      userUpdateRole={userUpdateRole}
      updateRoleHandler={updateRoleHandler}
    />
  );
};

export default UsersListContainer;
